import {
  BoxV2 as Box,
  ButtonV2 as Button,
  DropdownV3,
  Flex,
  TagsInput,
  TextInputV2 as TextInput,
} from 'portal-commons';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';

import { FormLayout, FormSection, FormTitle } from './components';
import { MaxSenderIdCount } from './constants';
import { useSenderIdControllers } from './hooks';
import { SenderIdFormValues } from './types';
import {
  createRequiredValidator,
  isValidInput,
  isValidSenderId,
  isValidSenderIds,
} from '../../validators';
import { senderIdController, Trademark, SenderId } from '@/apis/core';
import { DcaOptions as dcaOptions } from '../../constants';

interface SenderIdFormProps {
  brandUid: string;
  trademarks: Trademark[];
  senderId?: SenderId;
  maxCount?: number;
  onCancel?: () => void;
  onSubmit?: () => void;
}

const SenderIdForm: FunctionComponent<SenderIdFormProps> = ({
  brandUid,
  maxCount = MaxSenderIdCount,
  trademarks,
  senderId,
  onCancel,
  onSubmit,
}) => {
  const initialSenderIds = useMemo(() => {
    const verifiedTrademarks = trademarks.filter(
      (t) => t.outcome?.toLowerCase() === 'verified'
    );
    const defaultTrademarkUid =
      verifiedTrademarks.length === 1 ? verifiedTrademarks[0].uid : '';

    if (senderId) {
      const assignedTrademarkUid = senderId?.trademarkUid || '';

      return [
        {
          senderId: senderId.senderId,
          associatedDcaList: senderId.associatedDcaList,
          protectedSenderIdList: senderId.protectedSenderIdList || '',
          trademarkUid: assignedTrademarkUid || defaultTrademarkUid,
        },
      ];
    }

    return [
      {
        senderId: '',
        associatedDcaList: '',
        protectedSenderIdList: '',
        trademarkUid: defaultTrademarkUid,
      },
    ];
  }, [senderId, trademarks]);

  const {
    control,
    formState: { isValid },
    handleSubmit,
    trigger,
  } = useForm<SenderIdFormValues>({
    mode: 'onTouched',
    defaultValues: {
      senderIds: initialSenderIds,
    },
  });

  useEffect(() => {
    if (senderId) {
      // to highlight the expired trademark
      trigger();
    }
  }, [senderId, trigger]);

  const { senderIds, addSenderId } = useSenderIdControllers(control);
  const [proceeding, setProceeding] = useState(false);

  const handleValid = async (values: SenderIdFormValues) => {
    setProceeding(true);
    if (senderId) {
      const response = await senderIdController.updateSenderId(
        brandUid,
        senderId.uid,
        {
          senderId: values.senderIds[0].senderId,
          associatedDcaList: values.senderIds[0].associatedDcaList,
          protectedSenderIdList: values.senderIds[0].protectedSenderIdList,
          trademarkUid: values.senderIds[0].trademarkUid,
        }
      );
      setProceeding(false);
      if (response) {
        onSubmit?.();
      }
    } else {
      const response = await senderIdController.createSenderIds(
        brandUid,
        values.senderIds
      );
      setProceeding(false);
      if (response.length > 0) {
        onSubmit?.();
      }
    }
  };

  const trademarkOptions = useMemo(() => {
    const verifiedOptions = trademarks
      .filter((t) => t.outcome?.toLowerCase() === 'verified')
      .map((t) => ({
        label: t.trademarkText,
        value: t.uid,
      }));

    if (senderId) {
      const initialUid = initialSenderIds[0]?.trademarkUid;
      const initialTrademark = trademarks.find((t) => t.uid === initialUid);
      const isInitialExpired =
        initialTrademark?.outcome?.toLowerCase() === 'expired';

      if (isInitialExpired && initialTrademark) {
        return [
          {
            label: `${initialTrademark.trademarkText} (Expired)`,
            value: initialTrademark.uid,
            disabled: true,
          },
          ...verifiedOptions,
        ];
      }
    }

    return verifiedOptions;
  }, [trademarks, senderId, initialSenderIds]);

  const getTrademarkNote = (errorMessage?: string) => {
    if (errorMessage) {
      return errorMessage;
    }

    if (!senderId) {
      return 'You must select a verified trademark';
    }

    const assignedTrademarkUid = senderId?.trademarkUid || '';
    const isPendingAssignment = !assignedTrademarkUid;
    const verifiedTrademarks = trademarks.filter(
      (t) => t.outcome?.toLowerCase() === 'verified'
    );

    if (isPendingAssignment && verifiedTrademarks.length === 1) {
      return "We've automatically selected your only verified trademark. Click Submit to complete assignment.";
    }

    return 'You must select a verified trademark to save.';
  };

  return (
    <form noValidate onSubmit={handleSubmit(handleValid)}>
      <FormLayout>
        <FormTitle
          title={senderId ? 'Edit Sender ID' : 'Add Sender ID'}
          description={
            senderId
              ? 'You must have Sender ID information to create a Letter of Attestation (LOA).'
              : 'You must have Sender ID information to create a Letter of Authorization (LOA).\nA maximum of 5 Sender IDs to Allow may be submitted.'
          }
        />
        <Box sx={{ color: 't.black90', fontSize: 'H100' }}>
          * Indicates Required Field
        </Box>
        {senderIds.map((item, index) => (
          <FormSection key={index}>
            <Controller
              control={control}
              name={`senderIds.${index}.trademarkUid`}
              rules={{
                validate: {
                  isRequired: createRequiredValidator(
                    'You must select a verified trademark'
                  ),
                  isNotExpired: (val) => {
                    const matched = trademarks.find((t) => t.uid === val);
                    if (
                      matched &&
                      matched.outcome?.toLowerCase() === 'expired'
                    ) {
                      return 'Please select a verified trademark to save.';
                    }
                    return true;
                  },
                },
              }}
              render={({
                field: { value, onChange, onBlur },
                fieldState: { error },
              }) => (
                <DropdownV3
                  required
                  error={!!error?.message}
                  label="Trademark"
                  options={trademarkOptions}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  data-testid="trademarkList"
                  note={getTrademarkNote(error?.message)}
                />
              )}
            />
            <div />
            <Controller
              control={control}
              name={`senderIds.${index}.senderId`}
              rules={{
                validate: {
                  isRequired: createRequiredValidator('Please enter Sender ID'),
                  isValidInput,
                  isValidSenderId,
                },
              }}
              render={({
                field: { value, onChange, onBlur },
                fieldState: { error },
              }) => (
                <TextInput
                  error={!!error?.message}
                  label="Sender ID to Allow*"
                  note={error?.message}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  data-testid="senderId"
                />
              )}
            />
            <Controller
              control={control}
              name={`senderIds.${index}.associatedDcaList`}
              rules={{
                validate: {
                  isRequired: createRequiredValidator(
                    'Please select Associated DCA'
                  ),
                },
              }}
              render={({
                field: { value, onChange, onBlur },
                fieldState: { error },
              }) => (
                <DropdownV3
                  required
                  error={!!error?.message}
                  label="Associated DCA"
                  note={error?.message}
                  options={dcaOptions}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  data-testid="associatedDcaList"
                />
              )}
            />
            <Controller
              control={control}
              name={`senderIds.${index}.protectedSenderIdList`}
              rules={{
                validate: {
                  isValidSenderIds,
                },
              }}
              render={({
                field: { value, onChange, onBlur },
                fieldState: { error },
              }) => {
                const tags = useMemo(
                  () => value.split(',').filter((tag) => !!tag),
                  [value]
                );
                return (
                  <TagsInput
                    sx={{ gridColumn: 'span 2' }}
                    error={!!error?.message}
                    label="Sender IDs to Block (Max 5)"
                    helperText={error?.message}
                    tagRegex={/^[0-9a-zA-Z ]+$/}
                    tags={tags}
                    onChange={(updatedTags) => {
                      onChange(updatedTags.join(','));
                      onBlur();
                    }}
                    data-testid="protectedSenderIdList"
                  />
                );
              }}
            />
            {!senderId &&
              index === senderIds.length - 1 &&
              senderIds.length < maxCount && (
                <Flex sx={{ gridColumn: 'span 2', marginTop: '-24px' }}>
                  <Button
                    size="lg"
                    shape="square"
                    variant="transparent"
                    startAdornment={faPlus}
                    onClick={() => {
                      const verifiedTrademarks = trademarks.filter(
                        (t) => t.outcome?.toLowerCase() === 'verified'
                      );
                      const defaultTrademarkUid =
                        verifiedTrademarks.length === 1
                          ? verifiedTrademarks[0].uid
                          : '';
                      addSenderId({
                        senderId: '',
                        associatedDcaList: '',
                        protectedSenderIdList: '',
                        trademarkUid: defaultTrademarkUid,
                      });
                    }}
                    data-testid="addSenderId"
                  >
                    Add Sender ID
                  </Button>
                </Flex>
              )}
          </FormSection>
        ))}
        <Flex sx={{ justifyContent: 'flex-end', gap: 'xl' }}>
          <Button
            size="lg"
            shape="square"
            variant="outline"
            color="secondary"
            disabled={proceeding}
            onClick={onCancel}
            data-testid="cancel"
          >
            Cancel
          </Button>
          <Button
            size="lg"
            shape="square"
            color="secondary"
            type="submit"
            disabled={!isValid || proceeding}
            data-testid="submit"
          >
            {proceeding ? 'Processing' : senderId ? 'Save' : 'Submit'}
          </Button>
        </Flex>
      </FormLayout>
    </form>
  );
};

export default SenderIdForm;
